export const JOB_FORM_STEPS = [
  {
    step: 1,
    title: 'Job details',
    description: 'Company, role, location, and job description'
  },
  {
    step: 2,
    title: 'Requirements',
    description: 'Skills, compensation, experience, and matching criteria'
  },
  {
    step: 3,
    title: 'Hiring setup',
    description: 'Status, recruiter, interview rounds, and benefits'
  }
];

export function validateJobWizardStep(step, form, user) {
  if (step === 1) {
    if (!form.title?.trim()) return 'Job title is required';
    if (
      !window.__PACE_HIRING__ &&
      (user?.role === 'admin' || user?.role === 'recruiter') &&
      !form.clientId
    ) {
      return 'Please select a company for this job';
    }
  }
  return null;
}

export function maxValidJobWizardStep(form, user) {
  for (let s = 1; s <= JOB_FORM_STEPS.length; s += 1) {
    if (validateJobWizardStep(s, form, user)) return s;
  }
  return JOB_FORM_STEPS.length;
}

export default function JobWizardStepBar({ currentStep, onStepChange, form, user }) {
  const maxReachable = maxValidJobWizardStep(form, user);

  const tryGoTo = target => {
    if (target < currentStep) {
      onStepChange(target);
      return;
    }
    for (let s = currentStep; s < target; s += 1) {
      const err = validateJobWizardStep(s, form, user);
      if (err) {
        alert(err);
        onStepChange(s);
        return;
      }
    }
    if (target <= maxReachable) onStepChange(target);
  };

  return (
    <nav className="mb-5" aria-label="Job form steps">
      <ol className="flex flex-col sm:flex-row sm:items-stretch gap-3 sm:gap-0">
        {JOB_FORM_STEPS.map((item, index) => {
          const isActive = currentStep === item.step;
          const isComplete = currentStep > item.step;
          const isClickable = item.step <= maxReachable || item.step < currentStep;

          return (
            <li key={item.step} className="flex flex-1 items-center min-w-0">
              {index > 0 && (
                <div
                  className={`hidden sm:block flex-1 h-0.5 mx-2 shrink-0 ${
                    isComplete ? 'bg-[hsl(var(--ds-primary))]' : 'bg-[hsl(var(--ds-border))]'
                  }`}
                  aria-hidden
                />
              )}
              <button
                type="button"
                onClick={() => isClickable && tryGoTo(item.step)}
                disabled={!isClickable}
                className={`flex items-start gap-2.5 text-left w-full rounded-xl px-2 py-1.5 -mx-2 transition-colors ${
                  isClickable ? 'hover:bg-[hsl(var(--ds-muted))] cursor-pointer' : 'cursor-default opacity-70'
                }`}
              >
                <span
                  className={`flex h-8 w-8 shrink-0 items-center justify-center text-sm font-semibold rounded-full border ${
                    isActive
                      ? 'border border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-primary)/0.08)] text-[hsl(var(--ds-primary))]'
                      : isComplete
                        ? 'bg-[hsl(var(--ds-primary)/0.06)] text-[hsl(var(--ds-primary))] border-[hsl(var(--ds-primary)/0.35)]'
                        : 'bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-muted-fg))] border-[hsl(var(--ds-border))]'
                  }`}
                >
                  {isComplete ? '✓' : item.step}
                </span>
                <span className="min-w-0 pt-0.5">
                  <span
                    className={`block text-sm font-medium truncate ${
                      isActive ? 'text-[hsl(var(--ds-primary))]' : 'text-[hsl(var(--ds-fg))]'
                    }`}
                  >
                    {item.title}
                  </span>
                  <span className="block text-[11px] text-[hsl(var(--ds-muted-fg))] leading-snug hidden md:block">
                    {item.description}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ol>
      <p className="text-xs text-[hsl(var(--ds-muted-fg))] mt-2 sm:hidden">
        Step {currentStep} of {JOB_FORM_STEPS.length}:{' '}
        {JOB_FORM_STEPS.find(s => s.step === currentStep)?.title}
      </p>
    </nav>
  );
}
